import {
  CurecallButton,
  CurecallPinInput,
  CurecallPasswordInput,
  Strength,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { TFormValue, TRedirectReason, EFormField } from '../_components/auth.type'
import { useForm, SubmitHandler } from 'react-hook-form'
import { useState, useEffect, ChangeEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AuthBackButton } from '../_components/back-button.component'
import useCredentials from '../_components/auth.providers'
import { useTranslation } from 'react-i18next'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useNotification } from 'src/providers/notification.provider'

const REQUIRED_FORM_FIELD_LIST: Record<TRedirectReason, Array<EFormField>> = {
  VALIDATE_EMAIL_INIT_PASSWORD: [
    EFormField.EMAIL,
    EFormField.PIN,
    EFormField.OLD_PASSWORD,
    EFormField.NEW_PASSWORD,
    EFormField.CONFIRM_PASSWORD,
  ],
  VALIDATE_EMAIL: [EFormField.EMAIL, EFormField.PIN, EFormField.PASSWORD],
  RESET_PASSWORD: [EFormField.EMAIL, EFormField.PIN, EFormField.NEW_PASSWORD, EFormField.CONFIRM_PASSWORD],
}

export default function Page() {
  const navigate = useLocalizedNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const email = searchParams.get('email') as string
  const redirectReason = searchParams.get('redirectReason') as TRedirectReason

  const { validateEmailInitPassword, resetPassword, validateEmail, generatePin } = useCredentials()

  const [passwordStrength, setPasswordStrength] = useState<Strength>()

  const { notify } = useNotification()

  const {
    formState: { errors },
    register,
    setValue,
    setError,
    handleSubmit,
  } = useForm<TFormValue>({
    defaultValues: { email },
  })

  const generateNewPin = async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      event.preventDefault()
      await generatePin(email)
      notify(
        {
          title: t('pages.auth.notification.generate-pin-title-success'),
          message: t('pages.auth.notification.generate-pin-message-success'),
          appearance: 'SUCCESS',
        },
        15000,
      )
    } catch {
      notify(
        {
          title: t('pages.auth.notification.generate-pin-title-error'),
          message: t('pages.auth.notification.generate-pin-message-error'),
          appearance: 'ERROR',
        },
        15000,
      )
    }
  }
  const onSubmit: SubmitHandler<TFormValue> = (values) => {
    if (!values) return
    if (['VALIDATE_EMAIL_INIT_PASSWORD', 'RESET_PASSWORD'].includes(redirectReason)) {
      if (passwordStrength !== 'STRONG') {
        setError('newPassword', { message: t('pages.auth.input.error') })
        return
      }
      if (values.newPassword !== values.confirmPassword) {
        setError('confirmPassword', { message: t('pages.auth.input.error') })
        return
      }
    }
    if (redirectReason === 'VALIDATE_EMAIL_INIT_PASSWORD') {
      validateEmailInitPassword({
        pin: values.pin!,
        email: values.email!,
        password: values.oldPassword!,
        newPassword: values.newPassword!,
      })
    }
    if (redirectReason === 'VALIDATE_EMAIL') {
      validateEmail({
        pin: values.pin!,
        email: values.email!,
        password: values.password!,
      })
    }
    if (redirectReason === 'RESET_PASSWORD') {
      resetPassword({
        pin: values.pin!,
        email: values.email!,
        password: values.newPassword!,
      })
    }
  }

  const HEADER_OPTIONS: Record<TRedirectReason, Record<'title' | 'description', string | undefined>> = {
    VALIDATE_EMAIL_INIT_PASSWORD: {
      title: t('pages.auth.title.validate-email-init-password'),
      description: t('pages.auth.description.reset-password'),
    },
    VALIDATE_EMAIL: {
      title: t('pages.auth.title.validate-email'),
      description: undefined,
    },
    RESET_PASSWORD: {
      title: t('pages.auth.title.reset-password'),
      description: t('pages.auth.description.reset-password'),
    },
  }

  useEffect(() => {
    if (!email || !redirectReason) {
      navigate('/login')
    }
  }, [redirectReason, navigate, email])

  return (
    <div className="c-flex c-justify-center c-overflow-auto">
        <div className="c-flex c-flex-col  c-gap-4 c-w-3/4">
          <AuthBackButton />

          <div>
            <h2 className="c-font-semibold">{HEADER_OPTIONS[redirectReason].title}</h2>
            <p className="c-flex c-flex-col  c-list-disc c-m-0 [&>li]:c-ml-4">
              {HEADER_OPTIONS[redirectReason].description}
            </p>
          </div>

          <form
            action="#"
            method="POST"
            onSubmit={handleSubmit(onSubmit)}
            className="c-w-full c-flex c-flex-col  c-gap-8 [&>*]:c-w-full"
          >
          {REQUIRED_FORM_FIELD_LIST[redirectReason].includes(EFormField.PIN) && (
            <div>
              <CurecallPinInput
                {...(register('pin'), { required: t('pages.auth.input.error') })}
                isRequired
                label={t('pages.auth.input.pin')}
                onChange={(pin) => setValue(EFormField.PIN, pin)}
                errorMessage={errors.pin && t('pages.auth.input.error')}
              />
              <button
                onClick={(e) => generateNewPin(e)}
                type="button"
                className="c-flex c-items-center c-gap-1 c-font-semibold c-text-primary-600 c-mt-3"
              >
                {t('pages.auth.cta.receive-new-pin-code')}
              </button>
            </div>
          )}

          {REQUIRED_FORM_FIELD_LIST[redirectReason].includes(EFormField.PASSWORD) && (
            <CurecallPasswordInput
              {...(register('password'), { isRequired: t('pages.auth.input.error') })}
              isRequired
              label={t('pages.auth.input.password')}
              placeholder={t('pages.auth.input.password')}
              errorMessage={errors.password && t('pages.auth.input.error')}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setValue(EFormField.PASSWORD, event.target.value.trim())
              }
            />
          )}

          {REQUIRED_FORM_FIELD_LIST[redirectReason].includes(EFormField.OLD_PASSWORD) && (
            <CurecallPasswordInput
              {...(register('oldPassword'), { isRequired: t('pages.auth.input.error') })}
              isRequired
              label={t('pages.auth.input.old-password')}
              placeholder={t('pages.auth.input.old-password')}
              errorMessage={errors.oldPassword && t('pages.auth.input.error')}
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setValue(EFormField.OLD_PASSWORD, event.target.value.trim())
              }
            />
          )}

          {REQUIRED_FORM_FIELD_LIST[redirectReason].includes(EFormField.NEW_PASSWORD) && (
            <>
              <CurecallPasswordInput
                {...(register('newPassword'), { isRequired: t('pages.auth.input.error') })}
                isRequired
                label={t('pages.auth.input.new-password')}
                placeholder={t('pages.auth.input.new-password')}
                strengthBarLabels={{
                  EMPTY: t('pages.auth.input.empty'),
                  WEAK: t('pages.auth.input.weak'),
                  MEDIUM: t('pages.auth.input.medium'),
                  STRONG: t('pages.auth.input.strong'),
                }}
                onPasswordStrengthChange={(strength) => setPasswordStrength(strength)}
                errorMessage={errors.newPassword && t('pages.auth.input.error')}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setValue(EFormField.NEW_PASSWORD, event.target.value.trim())
                }
              />
              <CurecallPasswordInput
                {...(register('confirmPassword'), { isRequired: t('pages.auth.input.error') })}
                isRequired
                label={t('pages.auth.input.confirm-password')}
                placeholder={t('pages.auth.input.confirm-password')}
                errorMessage={errors.confirmPassword && t('pages.auth.input.error')}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setValue(EFormField.CONFIRM_PASSWORD, event.target.value.trim())
                }
              />
            </>
          )}

          <CurecallButton className="c-mt-4" isLoading={false} type="submit" size={ECurecallSize.LARGE}>
            {redirectReason === 'RESET_PASSWORD'
              ? t('pages.auth.cta.reinitialize-my-password')
              : t('pages.auth.cta.activate-my-account')}
          </CurecallButton>
          </form>
        </div>
    </div>
  )
}
